import InputField from "components/Common/InputField"
import ThemeModal from "components/Common/ThemeModal"
import FormikCreatableField from "components/Common/FormikCreatableField"
import { useCallback } from "react"
import { toast } from "react-toastify"
import { Form, FormFeedback, Label, Button, Row, Col } from "reactstrap"
import * as Yup from "yup"
import { useFormik } from "formik"
import { useFilteredVehicleOptions } from "hooks/stockFilterHook"
import { useOnboardVehicleStock } from "hooks/stockHook"

// vehicle_info keys mirror the backend onboarding payload (VehicleOnboardingService)
const emptyVehicleInfo = () => ({
  chassis_no: "",
  engine_no: "",
  brand: "",
  model: "",
  model_year: "",
  color: "",
  cc: "",
  fuel_type: "",
  tyre_size: "",
  mileage: "",
})

const validationSchema = Yup.object({
  chassis_no: Yup.string().trim().required("Chassis number is required"),
  engine_no: Yup.string().nullable(),
  brand: Yup.string().nullable(),
  model: Yup.string().nullable(),
  model_year: Yup.string().nullable(),
  color: Yup.string().nullable(),
  cc: Yup.string().nullable(),
  fuel_type: Yup.string().nullable(),
  tyre_size: Yup.string().nullable(),
  mileage: Yup.number().typeError("Mileage must be numeric").nullable(),
})

/**
 * Centralized "quick-onboard a vehicle" modal — mirrors the mobile app's
 * QuickOnboardVehicleModal. Posts directly to /vehicle-stocks/onboard
 * (VehicleOnboardingService), independent of whatever parent form is open.
 * On success, hands the onboarded vehicle back via onOnboarded so the
 * caller can select it (e.g. set a VehicleIdSelect value).
 */
const QuickOnboardVehicleModal = ({ isOpen, toggle, onOnboarded }) => {
  const { mutateAsync: onboardVehicle, isPending } = useOnboardVehicleStock()

  const { brands, models, years, exteriorColors, fuelTypes, engineCCs, isLoading } =
    useFilteredVehicleOptions({ isAll: true })

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: emptyVehicleInfo(),
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit: async (values, { resetForm }) => {
      try {
        const res = await onboardVehicle(values)
        toast.success(res?.message ?? "Vehicle onboarded successfully.")
        resetForm({ values: emptyVehicleInfo() })
        onOnboarded?.(res?.data)
        toggle()
      } catch (error) {
        const msg = error?.response?.data?.message || "Failed to onboard vehicle."
        toast.error(msg)
      }
    },
  })

  const handleClose = useCallback(() => {
    formik.resetForm({ values: emptyVehicleInfo() })
    toggle()
  }, [formik, toggle])

  return (
    <ThemeModal isOpen={isOpen} toggle={handleClose} title="Add Vehicle" size="lg">
      <Form onSubmit={e => { e.preventDefault(); formik.handleSubmit() }}>
        <p className="text-muted small mb-3">
          Enter the full chassis number — the vehicle is onboarded into stock automatically on save.
        </p>
        <Row>
          <Col md={6}>
            <div className="mb-3">
              <Label htmlFor="chassis_no">
                Full Chassis No. <span className="text-danger">*</span>
              </Label>
              <InputField
                type="text"
                name="chassis_no"
                id="chassis_no"
                value={formik.values.chassis_no}
                onChange={formik.handleChange}
                invalid={formik.touched.chassis_no && !!formik.errors.chassis_no}
              />
              {formik.touched.chassis_no && formik.errors.chassis_no && (
                <FormFeedback type="invalid">{formik.errors.chassis_no}</FormFeedback>
              )}
            </div>
          </Col>
          <Col md={6}>
            <div className="mb-3">
              <Label htmlFor="engine_no">Engine No.</Label>
              <InputField
                type="text"
                name="engine_no"
                id="engine_no"
                value={formik.values.engine_no}
                onChange={formik.handleChange}
              />
            </div>
          </Col>
          <Col md={4}>
            <FormikCreatableField
              name="brand"
              label="Brand"
              formik={formik}
              data={brands}
              valueKey="name"
              labelKey="name"
              isLoading={isLoading}
              placeholder="Select or type brand"
            />
          </Col>
          <Col md={4}>
            <FormikCreatableField
              name="model"
              label="Model"
              formik={formik}
              data={models}
              valueKey="name"
              labelKey="name"
              isLoading={isLoading}
              placeholder="Select or type model"
            />
          </Col>
          <Col md={4}>
            <FormikCreatableField
              name="model_year"
              label="Year / Model"
              formik={formik}
              data={years}
              valueKey="name"
              labelKey="name"
              isLoading={isLoading}
              placeholder="Select or type year"
            />
          </Col>
          <Col md={4}>
            <FormikCreatableField
              name="color"
              label="Color"
              formik={formik}
              data={exteriorColors}
              valueKey="name"
              labelKey="name"
              isLoading={isLoading}
              placeholder="Select or type color"
            />
          </Col>
          <Col md={4}>
            <FormikCreatableField
              name="fuel_type"
              label="Fuel Type"
              formik={formik}
              data={fuelTypes}
              valueKey="name"
              labelKey="name"
              isLoading={isLoading}
              placeholder="Select or type fuel type"
            />
          </Col>
          <Col md={4}>
            <FormikCreatableField
              name="cc"
              label="Engine CC"
              formik={formik}
              data={engineCCs}
              valueKey="name"
              labelKey="name"
              isLoading={isLoading}
              placeholder="Select or type CC"
            />
          </Col>
          <Col md={6}>
            <div className="mb-3">
              <Label htmlFor="tyre_size">Tyre Size</Label>
              <InputField
                type="text"
                name="tyre_size"
                id="tyre_size"
                value={formik.values.tyre_size}
                onChange={formik.handleChange}
              />
            </div>
          </Col>
          <Col md={6}>
            <div className="mb-3">
              <Label htmlFor="mileage">Mileage (KM)</Label>
              <InputField
                type="number"
                name="mileage"
                id="mileage"
                value={formik.values.mileage}
                onChange={formik.handleChange}
                invalid={formik.touched.mileage && !!formik.errors.mileage}
              />
              {formik.touched.mileage && formik.errors.mileage && (
                <FormFeedback type="invalid">{formik.errors.mileage}</FormFeedback>
              )}
            </div>
          </Col>
        </Row>

        <div className="text-end mt-3">
          <Button color="success" type="submit" disabled={isPending}>
            {isPending ? "Saving..." : "Save Vehicle"}
          </Button>
        </div>
      </Form>
    </ThemeModal>
  )
}

export default QuickOnboardVehicleModal
